import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PhoneProfileAvatar from "../components/whatsapp/PhoneProfileAvatar";
import {
  getBusinessProfile,
  getPhoneHealth,
  getWebhookStatus,
  reconnectWebhook,
  syncPhoneHealth,
  updateBusinessProfile,
  uploadBusinessProfilePhoto,
} from "../apis/whatsappAccount";
import {
  formatMessagingLimit,
  formatNameStatus,
  formatQualityRating,
  nameStatusTone,
  qualityTone,
} from "../utils/phoneHealthLabels";
import { getCallSettings, updateCallSettings } from "../apis/calls";

const VERTICAL_OPTIONS = [
  { value: "", label: "Not set" },
  { value: "OTHER", label: "Other" },
  { value: "AUTO", label: "Automotive" },
  { value: "BEAUTY", label: "Beauty, Spa & Salon" },
  { value: "APPAREL", label: "Clothing & Apparel" },
  { value: "EDU", label: "Education" },
  { value: "ENTERTAIN", label: "Entertainment" },
  { value: "EVENT_PLAN", label: "Event Planning" },
  { value: "FINANCE", label: "Finance & Banking" },
  { value: "GROCERY", label: "Food & Grocery" },
  { value: "GOVT", label: "Public Service" },
  { value: "HOTEL", label: "Hotel & Lodging" },
  { value: "HEALTH", label: "Medical & Health" },
  { value: "NONPROFIT", label: "Non-profit" },
  { value: "PROF_SERVICES", label: "Professional Services" },
  { value: "RETAIL", label: "Shopping & Retail" },
  { value: "TRAVEL", label: "Travel & Transportation" },
  { value: "RESTAURANT", label: "Restaurant" },
];

function StatusPill({ tone = "neutral", children }) {
  const chip = { good: "g", warn: "a", bad: "r", neutral: "n" }[tone] || "n";
  return <span className={`ping-chip ${chip}`}>{children}</span>;
}

function Field({ label, value, onEdit, editing, children }) {
  return (
    <div className="waba-field">
      <div className="top">
        <span>{label}</span>
        {onEdit && !editing && (
          <button type="button" onClick={onEdit}>Edit</button>
        )}
      </div>
      {editing ? children : (
        <p>{value || <span style={{ color: "var(--text-3)" }}>Not set</span>}</p>
      )}
    </div>
  );
}

function FieldActions({ saving, onSave, onCancel }) {
  return (
    <div className="waba-actions">
      <button type="button" className="waba-mini-btn save" disabled={saving} onClick={onSave}>
        Save
      </button>
      <button type="button" className="waba-mini-btn" onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

const WhatsappPhoneDetail = () => {
  const { phoneId } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [webhookBusy, setWebhookBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [account, setAccount] = useState(null);
  const [profile, setProfile] = useState(null);
  const [webhook, setWebhook] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [history, setHistory] = useState([]);
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState({});
  const [nameHelpOpen, setNameHelpOpen] = useState(false);
  const [calling, setCalling] = useState(null);
  const [callingError, setCallingError] = useState("");
  const [callingBusy, setCallingBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [healthRes, profileRes, webhookRes, callingRes] = await Promise.all([
        getPhoneHealth(phoneId),
        getBusinessProfile(phoneId),
        getWebhookStatus(phoneId).catch(() => null),
        getCallSettings(phoneId).catch((err) => ({ __error: err })),
      ]);

      if (callingRes?.__error) {
        setCalling(null);
        setCallingError(
          callingRes.__error.response?.data?.message ||
            "Calling not available for this number"
        );
      } else if (callingRes?.data?.success) {
        setCalling(callingRes.data.data?.calling || callingRes.data.data || null);
        setCallingError("");
      }

      if (!healthRes.data?.success) {
        throw new Error(healthRes.data?.error || "Failed to load health");
      }
      setAccount(healthRes.data.data.account);
      setAlerts(healthRes.data.data.alerts || []);
      setHistory(healthRes.data.data.history || []);

      if (webhookRes?.data?.success) {
        setWebhook(webhookRes.data.data || null);
      } else {
        setWebhook(null);
      }

      if (profileRes.data?.success) {
        const raw = profileRes.data.data || {};
        const nestedRaw = raw.profileData;
        const nested = Array.isArray(nestedRaw)
          ? nestedRaw[0] || {}
          : nestedRaw && typeof nestedRaw === "object"
            ? nestedRaw
            : {};
        setProfile({
          ...nested,
          ...raw,
          about: raw.about || nested.about || nested.whatsAppAbout || raw.whatsAppAbout || "",
          address: raw.address || nested.address || "",
          description: raw.description || nested.description || "",
          email: raw.email || nested.email || "",
          vertical: raw.vertical || nested.vertical || "",
          websites: raw.websites || nested.websites || [],
          profile_picture_url:
            raw.profile_picture_url ||
            nested.profile_picture_url ||
            nested.whatsAppDisplayImage ||
            raw.whatsAppDisplayImage ||
            null,
        });
      } else {
        setProfile({});
      }
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Failed to load phone details"
      );
    } finally {
      setLoading(false);
    }
  }, [phoneId]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (key) => {
    setEditKey(key);
    if (key === "websites") {
      setDraft({
        websites: Array.isArray(profile?.websites)
          ? profile.websites.join("\n")
          : "",
      });
    } else {
      setDraft({ [key]: profile?.[key] || "" });
    }
  };

  const cancelEdit = () => {
    setEditKey(null);
    setDraft({});
  };

  const saveField = async () => {
    if (!editKey) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {};
      if (editKey === "websites") {
        payload.websites = String(draft.websites || "")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
          .slice(0, 2);
      } else {
        payload[editKey] = draft[editKey] ?? "";
      }
      const res = await updateBusinessProfile(phoneId, payload);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Update failed");
      }
      setNotice("Profile updated");
      cancelEdit();
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const onPhotoSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const res = await uploadBusinessProfilePhoto(phoneId, file);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Photo upload failed");
      }
      setNotice("Profile photo updated");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Photo upload failed");
    } finally {
      setSaving(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    setError("");
    setNotice("");
    try {
      const res = await syncPhoneHealth(phoneId);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Sync failed");
      }
      setNotice("Health synced from Meta");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const onReconnectWebhook = async () => {
    setWebhookBusy(true);
    setError("");
    setNotice("");
    try {
      const res = await reconnectWebhook(phoneId);
      if (!res.data?.success) {
        throw new Error(res.data?.error || "Webhook reconnect failed");
      }
      setWebhook(res.data.data || null);
      setNotice(
        res.data.data?.connected
          ? "Webhook connected"
          : "Webhook re-registered — verify the URL matches"
      );
    } catch (err) {
      setError(
        err.response?.data?.error || err.message || "Webhook reconnect failed"
      );
    } finally {
      setWebhookBusy(false);
    }
  };

  const onUpdateCalling = async (patch) => {
    setCallingBusy(true);
    setError("");
    setNotice("");
    try {
      // Always include current status — Meta expects a coherent calling object.
      const payload = {
        status: calling?.status || "DISABLED",
        ...patch,
      };
      const res = await updateCallSettings(phoneId, payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || "Failed to update call settings");
      }
      const fresh = await getCallSettings(phoneId);
      setCalling(fresh.data?.data?.calling || fresh.data?.data || null);
      setCallingError("");
      setNotice("Call settings updated");
    } catch (err) {
      setError(
        err.response?.data?.message || err.message || "Failed to update call settings"
      );
    } finally {
      setCallingBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="waba-detail" style={{ minHeight: 240, display: "grid", placeItems: "center" }}>
        <div className="dash-spinner" />
      </div>
    );
  }

  const displayNameStatus =
    account?.new_name_status &&
    account.new_name_status !== "NONE" &&
    account.new_name_status !== "APPROVED"
      ? account.new_name_status
      : account?.name_status;

  const categoryLabel =
    VERTICAL_OPTIONS.find((v) => v.value === profile?.vertical)?.label ||
    profile?.vertical;

  return (
    <div className="waba-detail">
      <div className="waba-detail-head">
        <div>
          <button type="button" className="waba-mini-btn" onClick={() => navigate("/waba")} style={{ marginBottom: 8 }}>
            ← All numbers
          </button>
          <h2>{account?.verified_name || "WhatsApp Number"}</h2>
          <p>{account?.phone_number}</p>
        </div>
        <button type="button" className="ping-btn ping-btn-primary ping-btn-sm" onClick={onSync} disabled={syncing}>
          <svg
            className={syncing ? "animate-spin" : ""}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            width="14"
            height="14"
            aria-hidden
          >
            <path d="M4 4v5h.6M20 20v-5h-.6M5 9A8 8 0 0 1 19.4 8M19 15A8 8 0 0 1 4.6 16" />
          </svg>
          {syncing ? "Syncing…" : "Sync from Meta"}
        </button>
      </div>

      {error && (
        <div className="ping-banner red" style={{ marginBottom: 12 }}>{error}</div>
      )}
      {notice && (
        <div className="ping-banner" style={{ marginBottom: 12, background: "var(--green-soft)", borderColor: "#B7E4D4" }}>{notice}</div>
      )}

      <div className="waba-detail-grid">
        <div className="waba-card waba-kpi">
          <h3>Quality</h3>
          <StatusPill tone={qualityTone(account?.quality_rating)}>
            {formatQualityRating(account?.quality_rating)}
          </StatusPill>
        </div>
        <div className="waba-card waba-kpi">
          <h3>Limit</h3>
          <div className="v">{formatMessagingLimit(account?.messaging_limit)}</div>
        </div>
        <div className="waba-card waba-kpi">
          <h3>Number</h3>
          <StatusPill
            tone={
              String(account?.phone_number_status || "").toUpperCase() === "CONNECTED"
                ? "good"
                : account?.phone_number_status
                  ? "warn"
                  : "neutral"
            }
          >
            {account?.phone_number_status || "—"}
          </StatusPill>
        </div>
        <div className="waba-card waba-kpi">
          <h3>Display name</h3>
          <StatusPill tone={nameStatusTone(displayNameStatus)}>
            {formatNameStatus(displayNameStatus)}
          </StatusPill>
        </div>

        <div className="waba-card waba-span-2">
          <h3>Identity</h3>
          <div className="waba-id">
            <PhoneProfileAvatar
              phoneId={phoneId}
              profilePictureUrl={profile?.profile_picture_url || account?.profile_picture_url}
              sizeClass="w-14 h-14"
            />
            <div>
              <p style={{ fontWeight: 700, fontSize: 15 }}>{account?.verified_name || "Unnamed"}</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoSelected} />
              <button
                type="button"
                className="waba-mini-btn"
                disabled={saving}
                onClick={() => fileRef.current?.click()}
                style={{ marginTop: 6 }}
              >
                {saving ? "Uploading…" : "Change photo"}
              </button>
            </div>
          </div>
          <div className="waba-fields">
            <Field label="Display name" value={account?.verified_name} onEdit={() => setNameHelpOpen(true)} />
            {account?.requested_verified_name && account?.new_name_status === "PENDING_REVIEW" && (
              <p className="waba-muted">Requested: {account.requested_verified_name} (under review)</p>
            )}
            {account?.name_rejection_reason && (
              <p className="waba-muted" style={{ color: "var(--red)" }}>Rejection: {account.name_rejection_reason}</p>
            )}
            <Field
              label="About"
              value={profile?.about}
              editing={editKey === "about"}
              onEdit={() => startEdit("about")}
            >
              <textarea rows={2} value={draft.about || ""} onChange={(e) => setDraft({ ...draft, about: e.target.value })} />
              <FieldActions saving={saving} onSave={saveField} onCancel={cancelEdit} />
            </Field>
            <Field
              label="Category"
              value={categoryLabel}
              editing={editKey === "vertical"}
              onEdit={() => startEdit("vertical")}
            >
              <select value={draft.vertical || ""} onChange={(e) => setDraft({ ...draft, vertical: e.target.value })}>
                {VERTICAL_OPTIONS.map((o) => (
                  <option key={o.value || "empty"} value={o.value}>{o.label}</option>
                ))}
              </select>
              <FieldActions saving={saving} onSave={saveField} onCancel={cancelEdit} />
            </Field>
          </div>
        </div>

        <div className="waba-card waba-span-2">
          <h3>Details</h3>
          <div className="waba-fields">
            <Field
              label="Description"
              value={profile?.description}
              editing={editKey === "description"}
              onEdit={() => startEdit("description")}
            >
              <textarea rows={2} value={draft.description || ""} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
              <FieldActions saving={saving} onSave={saveField} onCancel={cancelEdit} />
            </Field>
            <Field
              label="Address"
              value={profile?.address}
              editing={editKey === "address"}
              onEdit={() => startEdit("address")}
            >
              <textarea rows={2} value={draft.address || ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} />
              <FieldActions saving={saving} onSave={saveField} onCancel={cancelEdit} />
            </Field>
            <Field
              label="Email"
              value={profile?.email}
              editing={editKey === "email"}
              onEdit={() => startEdit("email")}
            >
              <textarea rows={1} value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} />
              <FieldActions saving={saving} onSave={saveField} onCancel={cancelEdit} />
            </Field>
            <Field
              label="Websites"
              value={Array.isArray(profile?.websites) && profile.websites.length ? profile.websites.join("\n") : null}
              editing={editKey === "websites"}
              onEdit={() => startEdit("websites")}
            >
              <textarea
                rows={2}
                placeholder={"https://example.com\nhttps://instagram.com/..."}
                value={draft.websites || ""}
                onChange={(e) => setDraft({ ...draft, websites: e.target.value })}
              />
              <p className="waba-muted">Up to 2 URLs, one per line</p>
              <FieldActions saving={saving} onSave={saveField} onCancel={cancelEdit} />
            </Field>
          </div>
        </div>

        <div className="waba-card waba-span-2">
          <div className="top" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Webhook</h3>
            <button type="button" className="waba-mini-btn" disabled={webhookBusy} onClick={onReconnectWebhook}>
              {webhookBusy ? "Updating…" : "Reconnect"}
            </button>
          </div>
          <StatusPill
            tone={webhook?.connected ? "good" : webhook?.configuredUrl ? "warn" : "bad"}
          >
            {webhook?.connected
              ? "Connected"
              : webhook?.configuredUrl
                ? "URL mismatch"
                : webhook
                  ? "Not connected"
                  : "Unknown"}
          </StatusPill>
          <p className="waba-muted" style={{ marginTop: 8, wordBreak: "break-all" }}>
            {webhook?.configuredUrl || "No webhook URL configured"}
          </p>
          {webhook?.expectedUrl && webhook?.configuredUrl && !webhook.connected && (
            <p className="waba-muted" style={{ color: "var(--amber)" }}>Expected: {webhook.expectedUrl}</p>
          )}
          {webhook?.error && (
            <p className="waba-muted" style={{ color: "var(--red)" }}>{webhook.error}</p>
          )}
        </div>

        <div className="waba-card waba-span-2">
          <h3>Calling</h3>
          {!calling && callingError ? (
            <p className="waba-muted">{callingError}</p>
          ) : (
            <div className="waba-fields">
              <div className="waba-field">
                <div className="top">
                  <span>Status</span>
                  <button
                    type="button"
                    className="waba-mini-btn"
                    disabled={callingBusy}
                    onClick={() =>
                      onUpdateCalling({
                        status: calling?.status === "ENABLED" ? "DISABLED" : "ENABLED",
                      })
                    }
                  >
                    {callingBusy ? "Updating…" : calling?.status === "ENABLED" ? "Disable" : "Enable"}
                  </button>
                </div>
                <StatusPill tone={calling?.status === "ENABLED" ? "good" : "neutral"}>
                  {calling?.status || "DISABLED"}
                </StatusPill>
              </div>
              <div className="waba-field">
                <div className="top">
                  <span>Callback requests</span>
                  <button
                    type="button"
                    className="waba-mini-btn"
                    disabled={callingBusy || calling?.status !== "ENABLED"}
                    onClick={() =>
                      onUpdateCalling({
                        callback_permission_status:
                          calling?.callback_permission_status === "ENABLED" ? "DISABLED" : "ENABLED",
                      })
                    }
                  >
                    {calling?.callback_permission_status === "ENABLED" ? "Disable" : "Enable"}
                  </button>
                </div>
                <StatusPill tone={calling?.callback_permission_status === "ENABLED" ? "good" : "neutral"}>
                  {calling?.callback_permission_status || "DISABLED"}
                </StatusPill>
              </div>
              <div className="waba-field">
                <div className="top"><span>Call icon</span></div>
                <select
                  value={calling?.call_icon_visibility || "DEFAULT"}
                  disabled={callingBusy || calling?.status !== "ENABLED"}
                  onChange={(e) => onUpdateCalling({ call_icon_visibility: e.target.value })}
                >
                  <option value="DEFAULT">Default (show call icon)</option>
                  <option value="DISABLE_ALL">Hidden (no call icon)</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="waba-card waba-span-2">
          <h3>Alerts</h3>
          {alerts.length === 0 ? (
            <p className="waba-muted">No active alerts</p>
          ) : (
            <div className="waba-list">
              {alerts.map((a) => (
                <div key={a.id}>
                  <p style={{ fontWeight: 600, fontSize: 13.5 }}>{a.title}</p>
                  <p className="waba-muted">{new Date(a.created_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="waba-card waba-span-2">
          <h3>History</h3>
          {history.length === 0 ? (
            <p className="waba-muted">No events yet — try Sync from Meta</p>
          ) : (
            <div className="waba-list">
              {history.map((h) => (
                <div key={h.id} style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div>
                    <p style={{ fontSize: 13.5 }}>{h.title}</p>
                    <p className="waba-muted">{h.event_type}</p>
                  </div>
                  <p className="waba-muted" style={{ whiteSpace: "nowrap" }}>
                    {new Date(h.created_at).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {nameHelpOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Request Display Name Change
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              WhatsApp display names go through Meta review. Submit the change in
              WhatsApp Manager, then we&apos;ll update status here via the{" "}
              <code className="text-xs bg-gray-100 px-1 rounded">
                phone_number_name_update
              </code>{" "}
              webhook (Approved / Rejected).
            </p>
            <ol className="text-sm text-gray-700 list-decimal pl-5 space-y-1 mb-5">
              <li>Open Meta WhatsApp Manager</li>
              <li>Select this phone number → Profile</li>
              <li>Edit Display Name and submit for review</li>
              <li>Return here and click Sync if needed</li>
            </ol>
            <div className="flex gap-2 justify-end">
              <a
                href="https://business.facebook.com/wa/manage/phone-numbers/"
                target="_blank"
                rel="noreferrer"
                className="text-sm font-semibold px-4 py-2 rounded-xl bg-gray-900 text-white"
              >
                Open WhatsApp Manager
              </a>
              <button
                type="button"
                onClick={() => setNameHelpOpen(false)}
                className="text-sm font-semibold px-4 py-2 rounded-xl border border-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsappPhoneDetail;
